import { BaseService } from './BaseService';
import { DocumentService } from './integrations/DocumentService';
import { NotificationService } from './integrations/NotificationService';
import { AuditLogger } from '../utils/AuditLogger';
import { StateRequirement, StateRegistration } from '../types/registration';
import { PoolClient } from 'pg';
import { ComplianceRequirement } from '../types/compliance';

export class StateRegistrationService extends BaseService {
  private documentService: DocumentService;
  private notificationService: NotificationService;
  private auditLogger: AuditLogger;

  constructor() {
    super();
    this.documentService = new DocumentService();
    this.notificationService = new NotificationService();
    this.auditLogger = new AuditLogger();
  }

  // Add the executeQueryWithClient method with null check
  async executeQueryWithClient<T>(
    callback: (client: PoolClient) => Promise<T>
  ): Promise<T> {
    if (!this.pool) {
      throw new Error('Database pool is not initialized');
    }
    
    const client = await this.pool.connect();
    try {
      return await callback(client);
    } finally {
      client.release();
    }
  }

  async initializeRegistration(fundId: string, state: string): Promise<StateRegistration> {
    try {
      // Get state requirements
      const requirements = await this.getStateRequirements(state);
      
      // Check for exemptions
      const exemptionStatus = await this.checkExemptionEligibility(fundId, state);
      
      // Initialize registration record
      const registration = await this.executeQueryWithClient(async (client: PoolClient) => {
        const result = await client.query(
          `INSERT INTO state_registrations
           (fund_id, state, status, exemption_reason, requirements, initial_registration_date, renewal_date)
           VALUES ($1, $2, $3, $4, $5, $6, $7)
           RETURNING *`,
          [
            fundId,
            state,
            exemptionStatus.isExempt ? 'EXEMPT' : 'PENDING',
            exemptionStatus.reason,
            JSON.stringify(requirements),
            new Date(),
            this.calculateRenewalDate(state)
          ]
        );
        return result.rows[0] as StateRegistration;
      });

      // Generate required documents
      await this.initializeRequiredDocuments(registration.id, requirements);

      // Set up monitoring
      await this.setupComplianceMonitoring(registration.id);

      return registration;
    } catch (error) {
      this.auditLogger.error('Registration initialization failed', {
        fundId,
        state,
        error
      });
      throw error;
    }
  }

  async processMultiStateRegistration(fundId: string, states: string[]): Promise<void> {
    try {
      // Begin transaction
      await this.withTransaction(async (client: PoolClient) => {
        for (const state of states) {
          await this.initializeRegistration(fundId, state);
        }
        
        // Set up bulk monitoring
        await this.setupBulkComplianceMonitoring(fundId, states);
      });
    } catch (error) {
      throw error;
    }
  }

  async generateRequiredForms(registrationId: string): Promise<void> {
    const registration = await this.getRegistration(registrationId);
    const formTemplates = await this.getStateFormTemplates(registration.state);
    
    for (const template of formTemplates) {
      await this.documentService.getDocument(template.id);
    }
  }

  async monitorDeadlines(registrationId: string): Promise<void> {
    const registration = await this.getRegistration(registrationId);
    const deadlinesPromise = this.calculateFilingDeadlines(registration);
    
    const deadlines = await deadlinesPromise;
    
    for (const deadline of deadlines) {
      await this.notificationService.sendDeadlineReminder(
        registration.fundId,
        deadline as ComplianceRequirement
      );
    }
  }

  async processRenewal(registrationId: string): Promise<void> {
    try {
      const registration = await this.getRegistration(registrationId);
      
      // Verify eligibility
      await this.verifyRenewalEligibility(registration);
      
      // Generate renewal documents
      const renewalDocs = await this.generateRenewalDocuments(registration);
      
      // Calculate fees
      const fees = await this.calculateRenewalFees(registration);
      
      // Update registration status
      await this.updateRegistrationStatus(registrationId, 'RENEWAL_IN_PROGRESS');
      
      // Schedule follow-ups
      await this.scheduleRenewalFollowUp(registrationId);
    } catch (error) {
      this.auditLogger.error('Renewal processing failed', {
        registrationId,
        error
      });
      throw error;
    }
  }

  // Implementation of required methods
  private async getStateRequirements(state: string): Promise<StateRequirement[]> {
    // Implementation to fetch state requirements from database or static data
    return this.executeQueryWithClient(async (client: PoolClient) => {
      const result = await client.query(
        'SELECT * FROM state_requirements WHERE state_code = $1',
        [state]
      );
      return result.rows as StateRequirement[];
    });
  }

  private async checkExemptionEligibility(fundId: string, state: string): Promise<{ isExempt: boolean; reason?: string }> {
    // Implementation to check if organization is exempt from registration in this state
    return { isExempt: false };
  }

  private calculateRenewalDate(state: string): Date {
    // Implementation to calculate renewal date based on state rules
    const renewalDate = new Date();
    renewalDate.setFullYear(renewalDate.getFullYear() + 1);
    return renewalDate;
  }

  private async initializeRequiredDocuments(registrationId: string, requirements: StateRequirement[]): Promise<void> {
    // Implementation to set up required documents
  }

  private async setupComplianceMonitoring(registrationId: string): Promise<void> {
    // Implementation to set up compliance monitoring
  }

  private async setupBulkComplianceMonitoring(fundId: string, states: string[]): Promise<void> {
    // Implementation to set up bulk compliance monitoring
  }

  private async getRegistration(registrationId: string): Promise<StateRegistration> {
    return this.executeQueryWithClient(async (client: PoolClient) => {
      const result = await client.query(
        'SELECT * FROM state_registrations WHERE id = $1',
        [registrationId]
      );
      return result.rows[0] as StateRegistration;
    });
  }

  private async getStateFormTemplates(state: string): Promise<any[]> {
    // Implementation to get state form templates
    return [];
  }

  private async calculateFilingDeadlines(registration: StateRegistration): Promise<any[]> {
    // Implementation to calculate filing deadlines
    return [];
  }

  private async verifyRenewalEligibility(registration: StateRegistration): Promise<void> {
    // Implementation to verify renewal eligibility
  }

  private async generateRenewalDocuments(registration: StateRegistration): Promise<any[]> {
    // Implementation to generate renewal documents
    return [];
  }

  private async calculateRenewalFees(registration: StateRegistration): Promise<number> {
    // Implementation to calculate renewal fees
    return 0;
  }

  private async updateRegistrationStatus(registrationId: string, status: string): Promise<void> {
    await this.executeQueryWithClient(async (client: PoolClient) => {
      await client.query(
        'UPDATE state_registrations SET status = $1, updated_at = NOW() WHERE id = $2',
        [status, registrationId]
      );
    });
  }

  private async scheduleRenewalFollowUp(registrationId: string): Promise<void> {
    // Implementation to schedule renewal follow-up
  }
} 